import { clamp } from "@/utils/utils";
import type { MaybeRef } from "@vueuse/core";
import type { Ref } from "vue";
import { watch, ref } from "vue";
import { useSettingsStore } from "@/store";

/**
 * Get the interval for a range surrounding a value
 * @param value Center of the range
 * @param distance Distance of the range from the center in both direction
 * @param step Nice number for rounded ranges
 * @param min Minimum value of the range
 * @param max Maximum value of the range
 * @returns The interval surrounding the value without extending beyond the min or max
 */
export const getIntervalAround = (
  value: number,
  distance: number,
  step = 1,
  min = -Infinity,
  max = Infinity
): [number, number] => {
  const roundValue = Math.round(value / step) * step;
  return [
    clamp(roundValue - distance * step, min, max),
    clamp(roundValue + distance * step, min, max)
  ];
};

export const useDefaultPumpFrequencyRange = (
  frequency: Ref<number>,
  options?: {
    reactiveUpdates: MaybeRef<boolean>;
  }
): {
  frequencyRange: Ref<[number, number]>;
} => {
  const { flags } = useSettingsStore();
  const _reactiveUpdates = ref(options?.reactiveUpdates || false);

  const frequencyRange = ref<[number, number]>(
    getIntervalAround(
      frequency.value,
      2,
      5,
      flags.value.minimumPumpChartFrequency,
      flags.value.maximumPumpChartFrequency
    )
  );

  watch(
    () => frequency.value,
    val => {
      if (_reactiveUpdates.value) {
        frequencyRange.value = getIntervalAround(
          val,
          2,
          5,
          flags.value.minimumPumpChartFrequency,
          flags.value.maximumPumpChartFrequency
        );
      }
    },
    { deep: true }
  );

  return {
    frequencyRange
  };
};
